import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageContainer from '../components/common/PageContainer';
import authService from '../../services/api/authService';

/**
 * LoginRegisterPage - Login and registration page.
 * Uses authService for login/register (RULE: centralize API usage).
 */
const LoginRegisterPage = () => {
  const navigate = useNavigate();
  
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  
  const [registerData, setRegisterData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const [loginError, setLoginError] = useState('');
  const [registerError, setRegisterError] = useState('');
  const [loginSubmitting, setLoginSubmitting] = useState(false);
  const [registerSubmitting, setRegisterSubmitting] = useState(false);

  const handleLoginChange = (e) => {
    const { name, value, type, checked } = e.target;
    setLoginData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setLoginError('');
  };

  const handleRegisterChange = (e) => {
    const { name, value } = e.target;
    setRegisterData(prev => ({
      ...prev,
      [name]: value
    }));
    setRegisterError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginSubmitting(true);
    setLoginError('');

    try {
      await authService.login({
        email: loginData.email,
        password: loginData.password
      });

      window.dispatchEvent(new Event('userLoggedIn'));

      const returnUrl = new URLSearchParams(window.location.search).get('return') || '/store';
      navigate(returnUrl);
    } catch (error) {
      const msg =
        error.message ||
        error.response?.data?.message ||
        error.response?.data?.detail ||
        'Invalid email or password. Please try again.';
      setLoginError(
        error.type === 'oauth_account' || error.errors?.code === 'oauth_account'
          ? `${msg} Sign in with Google, or set a password under Profile → Security after signing in.`
          : msg
      );
    } finally {
      setLoginSubmitting(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegisterSubmitting(true);
    setRegisterError('');

    // Validation
    if (registerData.password !== registerData.confirmPassword) {
      setRegisterError('Passwords do not match');
      setRegisterSubmitting(false);
      return;
    }

    if (registerData.password.length < 8) {
      setRegisterError('Password must be at least 8 characters long');
      setRegisterSubmitting(false);
      return;
    }

    try {
      const data = await authService.register({
        email: registerData.email,
        password: registerData.password,
        first_name: registerData.firstName,
        last_name: registerData.lastName
      });

      if (data?.access) {
        localStorage.setItem('access_token', data.access);
        if (data.refresh) {
          localStorage.setItem('refresh_token', data.refresh);
        }
        window.dispatchEvent(new Event('userLoggedIn'));
      }

      alert('Registration successful! Welcome!');
      navigate('/store');
    } catch (error) {
      setRegisterError(
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.response?.data?.email?.[0] ||
        'Registration failed. Please try again.'
      );
    } finally {
      setRegisterSubmitting(false);
    }
  };

  return (
    <PageContainer>
      {/* Breadcrumb */}
      <div className="breadcrumb-area">
        <div className="container">
          <div className="breadcrumb-content">
            <ul>
              <li><Link to="/store">Home</Link></li>
              <li className="active">Login Register</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Login Content Area */}
      <div className="page-section mb-60">
        <div className="container">
          <div className="row">
            {/* Login Form */}
            <div className="col-sm-12 col-md-12 col-xs-12 col-lg-6 mb-30">
              <form onSubmit={handleLogin}>
                <div className="login-form">
                  <h4 className="login-title">Login</h4>
                  {loginError && (
                    <div className="alert alert-danger" role="alert">
                      {loginError}
                    </div>
                  )}
                  <div className="row">
                    <div className="col-md-12 col-12 mb-20">
                      <label>Email Address*</label>
                      <input
                        className="mb-0"
                        type="email"
                        name="email"
                        placeholder="Email Address"
                        value={loginData.email}
                        onChange={handleLoginChange}
                        required
                      />
                    </div>
                    <div className="col-12 mb-20">
                      <label>Password</label>
                      <input
                        className="mb-0"
                        type="password"
                        name="password"
                        placeholder="Password"
                        value={loginData.password}
                        onChange={handleLoginChange}
                        required
                      />
                    </div>
                    <div className="col-md-8">
                      <div className="check-box d-inline-block ml-0 ml-md-2 mt-10">
                        <input
                          type="checkbox"
                          id="remember_me"
                          name="rememberMe"
                          checked={loginData.rememberMe}
                          onChange={handleLoginChange}
                        />
                        <label htmlFor="remember_me">Remember me</label>
                      </div>
                    </div>
                    <div className="col-md-4 mt-10 mb-20 text-left text-md-right">
                      <Link to="/store/login-register">Forgotten password?</Link>
                    </div>
                    <div className="col-md-12">
                      <button
                        type="submit"
                        className="register-button mt-0"
                        disabled={loginSubmitting}
                      >
                        {loginSubmitting ? 'Logging in...' : 'Login'}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>

            {/* Register Form */}
            <div className="col-sm-12 col-md-12 col-lg-6 col-xs-12">
              <form onSubmit={handleRegister}>
                <div className="login-form">
                  <h4 className="login-title">Register</h4>
                  {registerError && (
                    <div className="alert alert-danger" role="alert">
                      {registerError}
                    </div>
                  )}
                  <div className="row">
                    <div className="col-md-6 col-12 mb-20">
                      <label>First Name</label>
                      <input
                        className="mb-0"
                        type="text"
                        name="firstName"
                        placeholder="First Name"
                        value={registerData.firstName}
                        onChange={handleRegisterChange}
                        required
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-20">
                      <label>Last Name</label>
                      <input
                        className="mb-0"
                        type="text"
                        name="lastName"
                        placeholder="Last Name"
                        value={registerData.lastName}
                        onChange={handleRegisterChange}
                        required
                      />
                    </div>
                    <div className="col-md-12 mb-20">
                      <label>Email Address*</label>
                      <input
                        className="mb-0"
                        type="email"
                        name="email"
                        placeholder="Email Address"
                        value={registerData.email}
                        onChange={handleRegisterChange}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-20">
                      <label>Password</label>
                      <input
                        className="mb-0"
                        type="password"
                        name="password"
                        placeholder="Password"
                        value={registerData.password}
                        onChange={handleRegisterChange}
                        required
                        minLength="8"
                      />
                    </div>
                    <div className="col-md-6 mb-20">
                      <label>Confirm Password</label>
                      <input
                        className="mb-0"
                        type="password"
                        name="confirmPassword"
                        placeholder="Confirm Password"
                        value={registerData.confirmPassword}
                        onChange={handleRegisterChange}
                        required
                        minLength="8"
                      />
                    </div>
                    <div className="col-12">
                      <button
                        type="submit"
                        className="register-button mt-0"
                        disabled={registerSubmitting}
                      >
                        {registerSubmitting ? 'Registering...' : 'Register'}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default LoginRegisterPage;
